/**
 * ZIP Processor Helpers
 * Handles extraction and processing of ZIP archives containing Pencil/Tweenly files
 */

import JSZip from "jszip"

/**
 * Type of content found in a ZIP archive
 */
export type ZipContentType = "pencil" | "tweenly" | "none"

/**
 * Contents extracted from a ZIP archive
 */
export type ZipContents = {
    /** Image blobs keyed by their path in the ZIP */
    imageBlobs: Map<string, Blob>
    /** Font blobs keyed by their path in the ZIP */
    fontBlobs: Map<string, Blob>
} & (
    | { type: "pencil"; content: string }
    | { type: "tweenly"; content: string }
    | { type: "none"; content: null }
)

/**
 * Supported asset folder types in ZIP archives
 */
type AssetType = "images" | "fonts"

/**
 * Asset folder definitions - maps folder patterns to asset types
 */
const ASSET_FOLDERS: { pattern: string; type: AssetType; prefix: string }[] = [
    { pattern: "assets/images/", type: "images", prefix: "images/" },
    { pattern: "images/", type: "images", prefix: "images/" },
    { pattern: "assets/fonts/", type: "fonts", prefix: "assets/fonts/" },
]

/**
 * Check if a ZIP entry path should be skipped (macOS metadata files)
 */
const shouldSkipEntry = (path: string): boolean => {
    return path.startsWith("__MACOSX/") || path.includes("/._")
}

/**
 * Parse asset path and determine its type and relative path
 */
const parseAssetPath = (path: string): { type: AssetType; relativePath: string } | null => {
    const lowerPath = path.toLowerCase()

    for (const folder of ASSET_FOLDERS) {
        const folderIndex = lowerPath.indexOf(folder.pattern)
        if (folderIndex !== -1) {
            // Extract filename after the folder pattern
            const afterFolder = path.substring(folderIndex + folder.pattern.length)
            if (afterFolder) {
                return {
                    type: folder.type,
                    relativePath: folder.prefix + afterFolder,
                }
            }
        }
    }

    return null
}

/**
 * Extract contents from a ZIP file.
 * Finds .pen, .tw/.json files and extracts images and fonts.
 *
 * NOTE: All assets are loaded into memory at once. For very large ZIPs with many
 * images this could cause memory issues. Future optimization could use streaming
 * or lazy-load blobs only when needed during upload.
 *
 * @param file - The ZIP file to extract
 * @returns Extracted contents including file content and asset blobs
 */
export const extractZipContents = async (file: File): Promise<ZipContents> => {
    const zip = await JSZip.loadAsync(file)

    let penContent: string | null = null
    let twContent: string | null = null
    const imageBlobs = new Map<string, Blob>()
    const fontBlobs = new Map<string, Blob>()

    for (const [path, zipEntry] of Object.entries(zip.files)) {
        // Skip directories and macOS metadata
        if (zipEntry.dir || shouldSkipEntry(path)) {
            continue
        }

        // Extract .pen file
        if (path.endsWith(".pen")) {
            penContent = await zipEntry.async("string")
            continue
        }

        // Extract .tw or .json file (Tweenly export)
        if (path.endsWith(".tw") || path.endsWith(".json")) {
            twContent = await zipEntry.async("string")
            continue
        }

        // Extract assets (images, fonts) based on folder structure
        const asset = parseAssetPath(path)
        if (asset) {
            const blob = await zipEntry.async("blob")
            switch (asset.type) {
                case "images":
                    imageBlobs.set(asset.relativePath, blob)
                    break
                case "fonts":
                    fontBlobs.set(asset.relativePath, blob)
                    break
            }
        }
    }

    // Prefers .tw over .pen (Tweenly native format preserves more data)
    if (twContent) {
        return { type: "tweenly", content: twContent, imageBlobs, fontBlobs }
    }
    if (penContent) {
        return { type: "pencil", content: penContent, imageBlobs, fontBlobs }
    }
    return { type: "none", content: null, imageBlobs, fontBlobs }
}
